import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Trash2, Search, Loader2, CheckCircle } from 'lucide-react';

const API = window.location.origin;

const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

export const StorageCleanupCard = ({ authHeaders }) => {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/api/admin/maintenance/orphaned-uploads`, authHeaders);
      setScan(res.data);
      if (res.data.count === 0) toast.success('No orphaned photos — storage is tidy!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm(`Remove ${scan.count} orphaned file${scan.count > 1 ? 's' : ''}? They'll no longer be accessible. Photos used anywhere on your site or documents are never touched.`)) return;
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/api/admin/maintenance/cleanup-uploads`, {}, authHeaders);
      toast.success(`Removed ${res.data.removed} orphaned files (${fmtBytes(res.data.total_bytes)})`);
      setScan({ count: 0, total_bytes: 0, orphans: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <Card data-testid="storage-cleanup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-orange-600" />
          Storage Cleanup
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Finds uploaded photos that aren't used anywhere (e.g. uploads you cancelled before saving) and removes them. Anything shown on your website, invoices or jobs is never touched. Uploads from the last 24 hours are left alone.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" onClick={runScan} disabled={scanning || cleaning} className="gap-1.5" data-testid="storage-scan-btn">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {scanning ? 'Scanning…' : 'Scan for orphaned photos'}
          </Button>
          {scan && scan.count > 0 && (
            <Button variant="destructive" onClick={runCleanup} disabled={cleaning} className="gap-1.5" data-testid="storage-cleanup-btn">
              {cleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Remove {scan.count} orphan{scan.count > 1 ? 's' : ''} ({fmtBytes(scan.total_bytes)})
            </Button>
          )}
        </div>
        {scan && (
          <div className="text-sm text-muted-foreground" data-testid="storage-scan-result">
            {scan.count === 0 ? (
              <span className="flex items-center gap-1.5 text-green-700"><CheckCircle className="h-4 w-4" /> No orphaned files found.</span>
            ) : (
              <span>{scan.count} orphaned file{scan.count > 1 ? 's' : ''} found, {fmtBytes(scan.total_bytes)} in total.</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StorageCleanupCard;
